import { hasPermission, Permission } from '@shared/rbac/permissions'
import type { PermissionName } from '@shared/rbac/permissions'
import { ACLPermissionEnum } from '../app/constants'
import type {
  MediaLibrarySearchCollectionResponse,
  MediaLibrarySearchFileResponse,
  MediaLibrarySearchFolderResponse,
  UserSessionResponse,
} from '../app/interfaces'
import type { FileDetailsResponse } from '../app/interfaces/FileDetailsResponse'
import { isRestrictedUser } from './roles'

/** FE alias for shared `Permission` — keeps call sites on the old name. */
export const PermissionsEnum = Permission
export type PermissionsEnum = PermissionName

export type { PermissionName }
export { Permission }

export const checkPermission = ({
  user,
  permission,
}: {
  user: UserSessionResponse | null
  permission: PermissionsEnum
}): boolean => hasPermission(user?.permissions, permission)

export const checkOperationPermission = ({
  user,
  aclPermission,
  selectedFiles,
  selectedFolders,
  selectedMediaCollections,
}: {
  user: UserSessionResponse | null
  aclPermission: ACLPermissionEnum[]
  selectedFiles: MediaLibrarySearchFileResponse[] | FileDetailsResponse[]
  selectedFolders: MediaLibrarySearchFolderResponse[]
  selectedMediaCollections: MediaLibrarySearchCollectionResponse[]
}): boolean => {
  if (!(user?.roles && isRestrictedUser(user.roles))) {
    return true
  }

  return ![
    ...selectedFiles,
    ...selectedFolders,
    ...selectedMediaCollections,
  ].filter(file => file.permission && !aclPermission.includes(file.permission))
    .length
}

export const checkOperationDisabled = ({
  user,
  aclPermission,
  selectedFiles,
  selectedFolders,
  selectedMediaCollections,
}: {
  user: UserSessionResponse | null
  aclPermission: ACLPermissionEnum[]
  selectedFiles: MediaLibrarySearchFileResponse[] | FileDetailsResponse[]
  selectedFolders: MediaLibrarySearchFolderResponse[]
  selectedMediaCollections: MediaLibrarySearchCollectionResponse[]
}): { isDisabled: boolean; remainingLength?: Number; assetName?: String } => {
  if (!(user?.roles && isRestrictedUser(user.roles))) {
    return { isDisabled: false, remainingLength: -1, assetName: '' }
  }

  const disableArray = [
    ...selectedFiles,
    ...selectedFolders,
    ...selectedMediaCollections,
  ]
    .filter(file => file.permission && aclPermission.includes(file.permission))
    .map(x => x.name)

  return {
    isDisabled: disableArray.length > 0,
    remainingLength: disableArray.length - 1,
    assetName: disableArray.length > 0 ? disableArray[0] : '',
  }
}

export const PermissionDescriptionMap = {
  [ACLPermissionEnum.MANAGE]: {
    label: 'Can manage',
    description_folder:
      'Full asset Management, including deleting, moving and sharing',
    description_file:
      'Full asset Management, including deleting, moving and sharing',
    description_collection:
      'Full asset Management including deleting and sharing',
    value: ACLPermissionEnum.MANAGE,
  },
  [ACLPermissionEnum.CONTRIBUTE]: {
    label: 'Can contribute',
    description_folder:
      'View, download, add and edit assets, and create sub-folders',
    description_file: 'View, download and edit file',
    description_collection: 'View, download and edit assets',
    value: ACLPermissionEnum.CONTRIBUTE,
  },
  [ACLPermissionEnum.READ]: {
    label: 'Can view',
    description_folder: 'View and download assets',
    description_file: 'View and download file',
    description_collection: 'View and download assets',
    value: ACLPermissionEnum.READ,
  },
}

export const aclPermissionOrderMapping = {
  [ACLPermissionEnum.READ]: 1,
  [ACLPermissionEnum.CONTRIBUTE]: 2,
  [ACLPermissionEnum.MANAGE]: 3,
}
